import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Ban, RotateCcw, Crown, Zap, RefreshCcw } from "lucide-react";
import { adminApi } from "@/admin/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: async () => (await adminApi.get("/users", { params: { q: q || undefined, limit: 100 } })).data,
    staleTime: 30_000,
  });

  const block = useMutation({
    mutationFn: async ({ id, reason }) =>
      (await adminApi.post(`/users/${id}/block`, null, { params: { reason } })).data,
    onSuccess: () => {
      toast.success("User blocked");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Block failed"),
  });
  const unblock = useMutation({
    mutationFn: async (id) => (await adminApi.post(`/users/${id}/unblock`)).data,
    onSuccess: () => {
      toast.success("User unblocked");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Unblock failed"),
  });
  const togglePro = useMutation({
    mutationFn: async ({ id, is_pro }) =>
      (await adminApi.post(`/users/${id}/pro`, { is_pro })).data,
    onSuccess: (d) => {
      toast.success(d.is_pro ? "Granted Totem Pro" : "Revoked Totem Pro");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-metrics"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const resetQuota = useMutation({
    mutationFn: async (id) => (await adminApi.post(`/users/${id}/ask-quota/reset`)).data,
    onSuccess: () => {
      toast.success("Ask FPL quota reset");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  const rows = data?.results || [];

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Admin</div>
        <h1 className="display-heading text-3xl">App Users</h1>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-totem-textDim" size={14} />
        <input
          data-testid="admin-users-search"
          placeholder="Search by manager ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-totem-surface border border-totem-border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-totem-green/60"
        />
      </div>

      <div className="totem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-totem-surface">
            <tr>
              <th className="text-left px-3 py-2 label-caps">Manager ID</th>
              <th className="text-left px-3 py-2 label-caps">Manager</th>
              <th className="text-left px-3 py-2 label-caps">Team</th>
              <th className="text-left px-3 py-2 label-caps">Overall rank</th>
              <th className="text-left px-3 py-2 label-caps">Status</th>
              <th className="text-left px-3 py-2 label-caps">Linked</th>
              <th className="text-left px-3 py-2 label-caps">Last login</th>
              <th className="text-left px-3 py-2 label-caps">Plan</th>
              <th className="text-left px-3 py-2 label-caps">Ask quota</th>
              <th className="text-right px-3 py-2 label-caps">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="p-4 text-center text-totem-textDim animate-pulse">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-totem-textDim">No users linked yet.</td></tr>
            )}
            {rows.map((u) => {
              const blocked = u.status === "blocked";
              const rank = u.overall_rank;
              return (
                <tr key={u.id} className="border-t border-totem-borderSoft" data-testid={`admin-user-row-${u.manager_id}`}>
                  <td className="px-3 py-2 mono font-semibold">{u.manager_id}</td>
                  <td className="px-3 py-2" data-testid="admin-user-manager-name">
                    {u.manager_name ? (
                      <span className="text-totem-text">{u.manager_name}</span>
                    ) : (
                      <span className="mono text-[10px] text-totem-textFaint">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-totem-textDim truncate max-w-[140px]">
                    {u.team_name || <span className="mono text-[10px] text-totem-textFaint">—</span>}
                  </td>
                  <td className="px-3 py-2 mono text-totem-green">
                    {rank ? rank.toLocaleString() : <span className="text-totem-textFaint">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        "mono text-[10px] uppercase px-2 py-0.5 rounded-full",
                        blocked
                          ? "bg-totem-red/15 text-totem-red border border-totem-red/40"
                          : "bg-totem-green/15 text-totem-green border border-totem-green/40"
                      )}
                    >
                      {blocked ? "Blocked" : "Active"}
                    </span>
                  </td>
                  <td className="px-3 py-2 mono text-[10px] text-totem-textDim">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 mono text-[10px] text-totem-textDim">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {u.is_pro ? (
                      <span className="mono text-[10px] uppercase px-2 py-0.5 rounded-full bg-totem-gold/15 text-totem-gold border border-totem-gold/40 inline-flex items-center gap-1">
                        <Crown size={10} /> Pro
                      </span>
                    ) : (
                      <span className="mono text-[10px] uppercase text-totem-textDim">Free</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.ask_quota ? (
                      u.ask_quota.is_pro ? (
                        <span className="mono text-[10px] uppercase text-totem-gold">∞</span>
                      ) : (
                        <span className="mono text-[10px] text-totem-textDim">
                          <span className={cn(
                            u.ask_quota.remaining === 0 && "text-totem-red",
                            u.ask_quota.remaining === 1 && "text-totem-gold",
                          )}>{u.ask_quota.used}</span>
                          /{u.ask_quota.limit}
                        </span>
                      )
                    ) : (
                      <span className="mono text-[10px] text-totem-textFaint">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => togglePro.mutate({ id: u.id, is_pro: !u.is_pro })}
                        data-testid={`admin-user-toggle-pro-${u.manager_id}`}
                        className={cn(
                          "mono text-[10px] uppercase inline-flex items-center gap-1 hover:brightness-110",
                          u.is_pro ? "text-totem-textDim" : "text-totem-gold"
                        )}
                        title={u.is_pro ? "Revoke Totem Pro" : "Grant Totem Pro"}
                      >
                        <Crown size={11} /> {u.is_pro ? "Revoke Pro" : "Grant Pro"}
                      </button>
                      {!u.is_pro && (
                        <button
                          onClick={() => resetQuota.mutate(u.id)}
                          data-testid={`admin-user-reset-quota-${u.manager_id}`}
                          className="mono text-[10px] uppercase text-totem-green inline-flex items-center gap-1 hover:brightness-110"
                          title="Reset Ask FPL weekly quota"
                        >
                          <RefreshCcw size={11} /> Reset
                        </button>
                      )}
                      {blocked ? (
                        <button
                          onClick={() => unblock.mutate(u.id)}
                          className="mono text-[10px] uppercase text-totem-green hover:brightness-110 inline-flex items-center gap-1"
                        >
                          <RotateCcw size={11} /> Unblock
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            const reason = window.prompt("Block reason (optional):");
                            block.mutate({ id: u.id, reason: reason || undefined });
                          }}
                          className="mono text-[10px] uppercase text-totem-red hover:brightness-110 inline-flex items-center gap-1"
                        >
                          <Ban size={11} /> Block
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-totem-textDim mono">
        Showing {rows.length} of {data?.total ?? "?"} linked managers.
      </div>
    </div>
  );
}
